import type { FC } from 'react';
import { Stack, Typography } from '@mui/material';
import type { Dayjs } from 'dayjs';

import { PriceGroupBadge } from '../../atoms/PriceGroupBadge';
import type { TStatusPillVariant } from '../../atoms/StatusPill/types';
import { MoplaDTicketAnim } from '../Animations';
import {
	MplDeutschlandTicketCardSeparator,
	MplDeutschlandTicketNameAndStatus,
	MplDeutschlandTicketRemainingTimeLeft,
	StyledAnimatedMoplaTicketingLogoWrapper,
	StyledBottomContainer,
	StyledCardContainer,
	StyledHeaderContainer,
	StyledTicketContainer,
	StyledTicketDetailsSection,
	StyledTicketFooterContainer,
	StyledTicketInfoSection,
} from '../MplDeutschlandTicketCard/variants/components';
import { MplTicketValidity } from '../MplTicketValidity';

import { OperatorLogo, Pricing, SecretWord, Zones } from './components';

export interface IMplTicketshopTicketCardProps {
	firstName: string;
	lastName: string;
	priceGroupName?: string;
	/** Total price paid, pre-formatted (e.g. "10,00 €"). */
	formattedPrice: string;
	vatNote?: string;
	statusText: string;
	statusVariant: TStatusPillVariant;
	validityLabel: string;
	validFrom: Dayjs;
	validUntil: Dayjs;
	stops?: string;
	validUntilShortTrip?: string;
	/**
	 * Tickets that are no longer usable (expired / cancelled): strikes through
	 * the validity value (`stops`, or the validFrom–validUntil range) and
	 * replaces the `remaining` countdown with this text.
	 */
	voidedText?: string;
	/** Cancelled tickets: strikes through the price. */
	isCancelled?: boolean;
	isDiscounted?: boolean;
	/** Optional countdown ("In X gültig" / "Noch X gültig"). */
	remaining?: { endDate: Dayjs; isPreordered: boolean };
	zonesLabel: string;
	/** Zone-based tickets: selected zone codes rendered as badges. */
	zoneCodes?: string[];
	/**
	 * BE flag, set when three or more zones were selected: the whole network is
	 * covered, so the zones show as a "first – last" range.
	 */
	isWholeNetwork?: boolean;
	wholeNetworkLabel?: string;
	ticketIdLabel: string;
	ticketCode: string;
	/** Inspection word (secretWord) badge text, e.g. "BIRKE". Omit to hide. */
	secretWord?: string;
	/** 48px operator logo shown in the footer. */
	operatorIconUrl?: string;
	dataTestId?: string;
}

/**
 * Read-only "notched ticket" card for a purchased ticketshop (regional tariff)
 * ticket. Reuses the Deutschlandticket card shell (container, side-notch
 * separator, name/status, countdown) and the shared validity row, and adds
 * ticketshop-specific bits: total price header, zone row, secret-word badge and
 * operator logo.
 */
export const MplTicketshopTicketCard: FC<IMplTicketshopTicketCardProps> = ({
	firstName,
	lastName,
	priceGroupName,
	formattedPrice,
	vatNote,
	statusText,
	statusVariant,
	validityLabel,
	validFrom,
	validUntil,
	stops,
	validUntilShortTrip,
	voidedText,
	isCancelled,
	isDiscounted,
	remaining,
	zonesLabel,
	zoneCodes,
	isWholeNetwork,
	wholeNetworkLabel,
	ticketIdLabel,
	ticketCode,
	secretWord,
	operatorIconUrl,
	dataTestId,
}) => {
	return (
		<StyledTicketContainer data-testid={dataTestId}>
			<StyledCardContainer>
				<StyledHeaderContainer>
					<StyledAnimatedMoplaTicketingLogoWrapper>
						<MoplaDTicketAnim />
					</StyledAnimatedMoplaTicketingLogoWrapper>
					<Pricing
						formattedPrice={formattedPrice}
						vatNote={vatNote}
						isCancelled={isCancelled}
						isDiscounted={isDiscounted}
					/>
				</StyledHeaderContainer>

				<MplDeutschlandTicketCardSeparator />

				<StyledBottomContainer>
					<StyledTicketDetailsSection>
						<Stack>
							<MplDeutschlandTicketNameAndStatus
								firstName={firstName}
								lastName={lastName}
								statusText={statusText}
								statusVariant={statusVariant}
							/>
							<PriceGroupBadge
								priceGroupName={priceGroupName}
								dataTestId="MplTicketshopTicketCard-price-group"
							/>
						</Stack>
						<Stack gap={0.5}>
							<MplTicketValidity
								validityLabel={validityLabel}
								validFrom={validFrom}
								validUntil={validUntil}
								value={stops}
								secondaryValue={validUntilShortTrip}
								isVoided={!!voidedText}
								dataTestIdPrefix="ticketshop"
							/>
							{voidedText ? (
								<Typography
									variant="l1"
									color="solutions.600"
									data-testid="ticketshop-validity-voided"
								>
									{voidedText}
								</Typography>
							) : (
								remaining && (
									<MplDeutschlandTicketRemainingTimeLeft
										endDate={remaining.endDate}
										isPreordered={remaining.isPreordered}
									/>
								)
							)}
							<Zones
								zonesLabel={zonesLabel}
								zoneCodes={zoneCodes}
								isWholeNetwork={isWholeNetwork}
								wholeNetworkLabel={wholeNetworkLabel}
							/>
						</Stack>
					</StyledTicketDetailsSection>

					<SecretWord secretWord={secretWord} />

					<StyledTicketFooterContainer>
						<StyledTicketInfoSection>
							<Typography variant="b2" color="solutions.600">
								{ticketIdLabel}
							</Typography>
							<Typography
								variant="b1"
								color="primary.main"
								data-testid="ticketshop-ticket-id"
							>
								{ticketCode || '-'}
							</Typography>
						</StyledTicketInfoSection>
						<OperatorLogo operatorIconUrl={operatorIconUrl} />
					</StyledTicketFooterContainer>
				</StyledBottomContainer>
			</StyledCardContainer>
		</StyledTicketContainer>
	);
};
